import React, { useState, useEffect } from "react";
import { Grid, Paper, Typography, Box, CircularProgress } from "@mui/material";
import "react-big-calendar/lib/css/react-big-calendar.css";
import ApexCharts from "react-apexcharts";
import { AllTypoFontStyles, adminDashboardStyles } from "../../Styles/styles";
import {
  CommissionChartIcon,
  CommissionEarnedIcon,
  EventsRegisteredIcon,
  TicketsSoldIcon,
  UsersRegisteredIcon,
} from "../../Svg/icons";
import AdminPieChart from "../../components/Dashboard/AdminCharts/PieChart/AdminPieChart";
import CustomCalendar from "../../components/CommonCustomCalender/CustomCalender";
import {
  useGetDashStatsQuery,
  useGetUserSalesQuery,
} from "../../Redux/apis/ApiSlice";
import { useNavigate } from "react-router-dom";

const AdminDashboard = () => {
  const [ApexChartsLoaded, setApexChartsLoaded] = useState(false);
  const { data: dashboardStats } = useGetDashStatsQuery();
  const { data: userSales, isLoading } = useGetUserSalesQuery();
  const [barSeries, setBarSeries] = useState([]);
  const soldTickets = dashboardStats?.totalTicketsSold;
  const totalTickets = dashboardStats?.totalTickets;
  const cardsData = [
    {
      title: "Total Events",
      value: dashboardStats?.totalEvents ? dashboardStats?.totalEvents : 0,
      icon: <UsersRegisteredIcon />,
    },
    {
      title: "Bookings",
      value: dashboardStats?.totalBooking ? dashboardStats.totalBooking : 0,
      icon: <EventsRegisteredIcon />,
    },
    {
      title: "Total Earning",
      value: dashboardStats?.totalAmount
        ? `$${dashboardStats?.totalAmount}`
        : 0,
      icon: <CommissionEarnedIcon />,
    },
  ];
  const [xAxisCategories, setXAxisCategories] = useState([]);

  useEffect(() => {
    import("react-apexcharts").then(() => {
      setApexChartsLoaded(true);
    });

    // Prepare the data for the chart
    const prepareChartData = () => {
      const salesByDate = userSales?.totalSalesByDate || {};
      const currentDate = new Date();
      const lastSevenDays = [];
      const dayMapping = ["S", "M", "T", "W", "Th", "F", "S"];

      for (let i = 6; i >= 0; i--) {
        const date = new Date(currentDate);
        date.setDate(date.getDate() - i);
        const formattedDate = date.toISOString().split("T")[0];
        const dayOfWeek = dayMapping[date.getDay()];
        lastSevenDays.push({
          date: formattedDate,
          day: dayOfWeek,
          sales: salesByDate[formattedDate] || 0,
        });
      }

      setXAxisCategories(lastSevenDays.map((d) => d.day));
      setBarSeries([
        {
          name: "Earning",
          data: lastSevenDays.map((d) => d.sales),
        },
      ]);
    };

    if (userSales?.totalSalesByDate) {
      prepareChartData();
    }
  }, [userSales]);

  const barOptions = {
    chart: {
      type: "bar",
      height: 200,
      toolbar: { show: false },
      parentHeightOffset: 0,
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: "20%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: false,
    },
    grid: {
      padding: {
        left: 10,
        right: 10,
      },
      row: {
        colors: ["#f3f3f3", "transparent"],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: xAxisCategories,
      labels: {
        rotate: -45,
        style: {
          fontSize: "12px",
        },
      },
      tickPlacement: "on",
    },
    yaxis: {
      labels: {
        formatter: (val) => `$${val}`,
      },
      title: {
        style: {
          fontWeight: 600,
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.25,
        opacityFrom: 0.9,
        opacityTo: 0.9,
        stops: [50, 100],
      },
    },
    colors: ["#2D6F8C"],
    responsive: [
      {
        breakpoint: 600,
        options: {
          chart: {
            height: 200,
          },
          xaxis: {
            labels: {
              rotate: -30,
              style: {
                fontSize: "10px",
              },
            },
          },
        },
      },
    ],
  };

  return (
    <>
      {/* <AddYourEventButton/> */}
      <Box sx={adminDashboardStyles.AdminDashboardMainBox}>
        <Grid container xl={12} lg={12} md={12} xs={12} spacing={1}>
          <Grid item xl={6} lg={6} md={12} xs={12}>
            <Grid container spacing={1}>
              {cardsData.map((item, index) => (
                <Grid item xs={12} sm={index === 2 ? 12 : 6} key={index}>
                  <Paper
                    elevation={1}
                    sx={adminDashboardStyles.AdminDashboardPaper}
                  >
                    <Typography sx={AllTypoFontStyles.CommonCardsTitleTypo}>
                      {item.title}
                    </Typography>
                    <Box sx={adminDashboardStyles.AdminDashboardBox1}>
                      <Grid>{item.icon}</Grid>
                      <Typography
                        sx={{ ...AllTypoFontStyles.CommonCardsValueTypo }}
                        variant="h2"
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xl={6} lg={6} md={12} xs={12}>
            <Grid
              container
              spacing={2}
              sx={adminDashboardStyles.AdminDashboardChartGrid}
            >
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={1}
                  sx={{
                    ...adminDashboardStyles.AdminDashboardChartPaper,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      // gap: { xl: 10, lg: 4 },
                    }}
                  >
                    <TicketsSoldIcon />
                    <Typography
                      sx={{
                        ...AllTypoFontStyles.CommonCardsTitleTypo,
                        ml: { xs: 1, sm: 1, md: 1 },
                      }}
                    >
                      Tickets Sold
                    </Typography>
                  </Box>
                  <Box sx={{ ...adminDashboardStyles?.AdminDashboardPieBox }}>
                    <AdminPieChart
                      totalSold={soldTickets || 0}
                      totalTickets={totalTickets || 0}
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={1}
                  sx={{
                    ...adminDashboardStyles.AdminDashboardChartPaper,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      // gap: { xl: 10, lg: 4 },
                    }}
                  >
                    <CommissionChartIcon />
                    <Typography
                      sx={{
                        ...AllTypoFontStyles.CommonCardsTitleTypo,
                        ml: { xs: 1, sm: 1, md: 1 },
                      }}
                    >
                      Earning
                    </Typography>
                  </Box>
                  {ApexChartsLoaded && (
                    <ApexCharts
                      options={barOptions}
                      series={barSeries}
                      type="bar"
                      height={200}
                    />
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} p={1}>
            <CustomCalendar />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default AdminDashboard;
